"use client";

import {useWorkspaceData} from "./workspace-context";
import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useEffect, useMemo, useState } from "react";
import {
  calculatePortfolio,
  POLYMARKET_PAPER_VENUE,
  MANUAL_PAPER_VENUE,
  isPaperPortfolioPosition,
  portfolioAssetTypes,
  portfolioSides,
  portfolioStatuses,
  type PortfolioAssetType,
  type PortfolioConnectionState,
  type WebullConnectionState,
  type PortfolioPosition,
  type PortfolioPositionInput,
  type PortfolioSide,
  type PortfolioStatus,
} from "@/lib/portfolio";
import type { KalshiConnectionState } from "@/lib/kalshi";
import type { CoinbaseConnectionState } from "@/lib/coinbase";
import { apiFetch } from "@/lib/client-api";
import CoinbaseTradeTicket from "./coinbase-trade-ticket";
import KalshiTradeTicket from "./kalshi-trade-ticket";
import PortfolioAutonomy from "./portfolio-autonomy";
import { useConfirmation } from "./use-confirmation";
import WebullConnection from './webull-connection';

type PortfolioWorkspaceProps = {
  positions: PortfolioPosition[];
  webullPaperConnection: PortfolioConnectionState;
  webullProductionConnection?: WebullConnectionState;
  polymarketConnection: PortfolioConnectionState;
  kalshiDemoConnection: KalshiConnectionState;
  kalshiProductionConnection: KalshiConnectionState;
  coinbasePaperConnection: CoinbaseConnectionState;
  coinbaseProductionConnection: CoinbaseConnectionState;
  loading: boolean;
  loadError: string | null;
  onReload: () => Promise<void>;
  onPositionsChange: (positions: PortfolioPosition[]) => void;
  focusId?: string | null;
};

type PortfolioEnvironment = "capital" | "paper";

type PositionDraft = {
  symbol: string;
  name: string;
  assetType: PortfolioAssetType;
  side: PortfolioSide;
  status: PortfolioStatus;
  quantity: string;
  unitCost: string;
  markPrice: string;
  venue: string;
  thesis: string;
  notes: string;
  riskLimitPct: string;
  sourceIds: string;
  systemIds: string;
  simulationIds: string;
  projectionIds: string;
  openedAt: string;
};

function blankDraft(): PositionDraft {
  return {
    symbol: "",
    name: "",
    assetType: "equity",
    side: "long",
    status: "active",
    quantity: "",
    unitCost: "",
    markPrice: "",
    venue: "",
    thesis: "",
    notes: "",
    riskLimitPct: "",
    sourceIds: "",
    systemIds: "",
    simulationIds: "",
    projectionIds: "",
    openedAt: "",
  };
}

function paperDraft(): PositionDraft {
  return {
    ...blankDraft(),
    assetType: "fund",
    venue: MANUAL_PAPER_VENUE,
    notes: "",
  };
}

function isEditablePosition(position: PortfolioPosition) {
  return position.origin === "manual" || position.origin === "manual-paper" || position.origin === "polymarket-paper";
}

function draftFromPosition(position: PortfolioPosition): PositionDraft {
  return {
    symbol: position.symbol,
    name: position.name,
    assetType: position.assetType,
    side: position.side,
    status: position.status,
    quantity: String(position.quantity),
    unitCost: position.unitCost === null ? "" : String(position.unitCost),
    markPrice: position.markPrice === null ? "" : String(position.markPrice),
    venue: position.venue,
    thesis: position.thesis,
    notes: position.notes,
    riskLimitPct: position.riskLimitPct === null ? "" : String(position.riskLimitPct),
    sourceIds: position.sourceIds.join(", "),
    systemIds: position.systemIds.join(", "),
    simulationIds: position.simulationIds.join(", "),
    projectionIds: position.projectionIds.join(", "),
    openedAt: position.openedAt ?? "",
  };
}

function nullableNumber(value: string) {
  return value.trim() === "" ? null : Number(value);
}

function linkedIds(value: string) {
  return Array.from(new Set(value.split(",").map((entry) => entry.trim()).filter(Boolean)));
}

function payloadFromDraft(
  draft: PositionDraft,
  environment: PortfolioEnvironment,
): PortfolioPositionInput {
  const sourceIds = linkedIds(draft.sourceIds);
  return {
    symbol: draft.symbol.trim().toUpperCase(),
    name: draft.name.trim() || draft.symbol.trim().toUpperCase(),
    assetType: draft.assetType,
    side: draft.side,
    status: draft.status,
    quantity: Number(draft.quantity || 0),
    unitCost: nullableNumber(draft.unitCost),
    markPrice: nullableNumber(draft.markPrice),
    currency: "USD",
    venue: environment === "paper" ? draft.venue === POLYMARKET_PAPER_VENUE ? POLYMARKET_PAPER_VENUE : MANUAL_PAPER_VENUE : draft.venue.trim(),
    thesis: draft.thesis.trim(),
    notes: draft.notes.trim(),
    riskLimitPct: nullableNumber(draft.riskLimitPct),
    sourceIds: environment === "paper" && draft.venue === POLYMARKET_PAPER_VENUE
      ? Array.from(new Set([...sourceIds, "polymarket"]))
      : sourceIds,
    systemIds: linkedIds(draft.systemIds),
    simulationIds: linkedIds(draft.simulationIds),
    projectionIds: linkedIds(draft.projectionIds),
    openedAt: draft.openedAt || null,
  };
}

function money(value: number | null) {
  if (value === null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(value) < 1_000 ? 2 : 0,
  }).format(value);
}

function number(value: number) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 }).format(value);
}

function percent(value: number | null) {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

function updatedLabel(value: string | null) {
  if (!value) return "No records";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return formatWorkspaceDate(date, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function PortfolioWorkspace({
  positions,
  webullPaperConnection,
  webullProductionConnection,
  polymarketConnection,
  kalshiDemoConnection,
  kalshiProductionConnection,
  coinbasePaperConnection,
  coinbaseProductionConnection,
  loading,
  loadError,
  onReload,
  onPositionsChange,
  focusId,
}: PortfolioWorkspaceProps) {
  const workspaceData=useWorkspaceData();
  const [portfolioSection,setPortfolioSection]=useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("engine") === "autonomy" ? "Autonomy" : "Investments");
  const [environment, setEnvironment] = useState<PortfolioEnvironment>(() => positions.some(position => position.id === focusId && isPaperPortfolioPosition(position)) ? "paper" : "capital");
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(focusId ?? null);
  useEffect(() => { window.scrollTo({top:0}); }, [selectedPositionId]);
  const { confirm, confirmationDialog } = useConfirmation();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PositionDraft>(blankDraft);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const visiblePositions = useMemo(() => positions.filter((position) => (
    environment === "paper" ? isPaperPortfolioPosition(position) : !isPaperPortfolioPosition(position)
  )), [environment, positions]);
  const { summary, metrics } = useMemo(
    () => calculatePortfolio(visiblePositions),
    [visiblePositions],
  );
  const latestUpdate = visiblePositions.reduce<string | null>((latest, position) => {
    const candidate = position.syncedAt ?? position.updatedAt;
    return !latest || Date.parse(candidate) > Date.parse(latest) ? candidate : latest;
  }, null);

  const environmentLabel = environment === "paper" ? "Paper" : "Capital";
  const kalshiConnection = environment === "paper"
    ? kalshiDemoConnection
    : kalshiProductionConnection;
  const coinbaseConnection = environment === "paper"
    ? coinbasePaperConnection
    : coinbaseProductionConnection;
  const activeTradingVenues = [
    environment === 'capital' && webullProductionConnection?.status === 'live' ? 'Webull' : null,
    coinbaseConnection.status === "live" ? "Coinbase" : null,
    kalshiConnection.status === "live" ? "Kalshi" : null,
  ].filter((venue): venue is string => Boolean(venue));
  const environmentStatus = activeTradingVenues.length
    ? `${activeTradingVenues.join(" + ")} ${environment === "paper" ? "paper ready" : "real connected"}`
    : environment === "paper"
      ? `${webullPaperConnection.message} Polymarket simulation enabled.`
      : polymarketConnection.status === "live"
        ? `${polymarketConnection.message} Orders remain read only.`
        : "Owner-entered capital · trading venues not connected";

  function setField<Key extends keyof PositionDraft>(key: Key, value: PositionDraft[Key]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function startNew() {
    setSelectedPositionId(null);
    setEditingId(null);
    setDraft(environment === "paper" ? paperDraft() : blankDraft());
    setMutationError(null);
    setEditorOpen(true);
  }

  function startEdit(position: PortfolioPosition) {
    if (!isEditablePosition(position)) return;
    setEditingId(position.id);
    setDraft(draftFromPosition(position));
    setMutationError(null);
    setEditorOpen(true);
    window.setTimeout(() => document.getElementById("portfolio-editor")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    }), 20);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingId(null);
    setDraft(blankDraft());
    setMutationError(null);
  }

  async function savePosition(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMutationError(null);
    try {
      const response = await apiFetch("/api/portfolio", {
        method: editingId ? "PATCH" : "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(editingId
          ? { id: editingId, ...payloadFromDraft(draft, environment) }
          : payloadFromDraft(draft, environment)),
      });
      const payload = await response.json() as { position?: PortfolioPosition; error?: string };
      if (!response.ok || !payload.position) {
        throw new Error(payload.error ?? "The position could not be saved.");
      }
      const next = editingId
        ? positions.map((position) => position.id === editingId ? payload.position! : position)
        : [payload.position, ...positions];
      onPositionsChange(next);
      closeEditor();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : "The position could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function removePosition(position: PortfolioPosition) {
    if (!isEditablePosition(position)) return;
    if (!await confirm(`Delete ${position.symbol} from the portfolio ledger? This removes this manual record.`)) return;
    setMutationError(null);
    try {
      const response = await apiFetch(`/api/portfolio?id=${encodeURIComponent(position.id)}`, {
        method: "DELETE",
        headers: { accept: "application/json" },
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error ?? "The position could not be deleted.");
      onPositionsChange(positions.filter((candidate) => candidate.id !== position.id));
      if (selectedPositionId === position.id) setSelectedPositionId(null);
      if (editingId === position.id) closeEditor();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : "The position could not be deleted.");
    }
  }

  return (
    <section className="workspace portfolio" data-mode={editorOpen ? "editor" : selectedPositionId ? "detail" : "overview"} data-empty={!visiblePositions.length && !activeTradingVenues.length} id="workspace" aria-labelledby="portfolio-title">
      {confirmationDialog}
      <header className="page-heading">
        <div>
          <p className="eyebrow">{environmentLabel} state</p>
          {(editorOpen || selectedPositionId) && <button type="button" className="back-link" onClick={() => { closeEditor(); setSelectedPositionId(null); }}>← Portfolio</button>}<h1 id="portfolio-title">{editorOpen ? editingId ? "Edit position" : environment === "paper" ? "Add paper position" : "Add manual record" : selectedPositionId ? positions.find(position => position.id === selectedPositionId)?.symbol : "Portfolio"}</h1>
          <p className="lede">Positions, marks, theses, and exposure in one governed ledger.</p>
        </div>
        {portfolioSection !== "Autonomy" && <div className="connection-summary" aria-live="polite">
          <strong>{loading ? `Loading ${environmentLabel}` : `${summary.activeCount} active · ${visiblePositions.length} records`}</strong>
          <span>{environmentStatus}</span>
        </div>}
      </header>

      {!editorOpen&&!selectedPositionId&&<nav className="quiet-tabs" aria-label="Portfolio sections">{["Investments","Autonomy","Contributions","Local exchanges"].map(section=><button type="button" key={section} aria-pressed={portfolioSection===section} onClick={()=>setPortfolioSection(section)}>{section}</button>)}</nav>}
      {portfolioSection === "Autonomy" ? <PortfolioAutonomy/> : portfolioSection!=="Investments"?<section className="portfolio-related-records"><h2>{portfolioSection}</h2><p className="muted">Draft amounts are separate from investment capital and verified payments.</p>{workspaceData.document.records.filter(r=>r.kind===(portfolioSection==="Contributions"?"contribution":"exchange")).map(record=><div className="plain-row" key={record.id}><div><strong>{record.title}</strong><p className="muted">{record.status}</p></div><span>{new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(record.amount)} proposed</span></div>)}<button type="button" onClick={()=>{location.hash=portfolioSection==="Contributions"?"contributions":"locallens";}}>Open {portfolioSection.toLowerCase()} →</button></section>:<>
      <div className="portfolio-controls">
        <div className="portfolio-environment-switch" role="group" aria-label="Portfolio environment">
          <button
            type="button"
            aria-pressed={environment === "capital"}
            onClick={() => {
              closeEditor();
              setEnvironment("capital");
            }}
          >
            Capital
          </button>
          <button
            type="button"
            aria-pressed={environment === "paper"}
            onClick={() => {
              closeEditor();
              setEnvironment("paper");
            }}
          >
            Paper
          </button>
        </div>
        <span>Updated {updatedLabel(latestUpdate)}</span>
        <div>
          <button type="button" onClick={() => void onReload()} disabled={loading}>Refresh</button>
          <button type="button" onClick={startNew}>{environment === "paper" ? "Add paper position" : "Add position"}</button>
        </div>
      </div>

      {(loadError || mutationError) && (
        <div className="service-error" role="alert">
          <span>{mutationError ?? loadError}</span>
          {loadError && <button type="button" onClick={() => void onReload()}>Try again</button>}
        </div>
      )}

      {environment === "paper" && webullPaperConnection.status !== "live" && !loadError && (
        <div className="portfolio-connection-note" role="status">
          <span>{webullPaperConnection.message}</span>
          <strong>Orders disabled</strong>
        </div>
      )}

      {environment === "capital" && polymarketConnection.status === "error" && !loadError && (
        <div className="portfolio-connection-note" role="status">
          <span>{polymarketConnection.message}</span>
          <strong>Read only · orders disabled</strong>
        </div>
      )}

      {environment === 'capital' && webullProductionConnection && <WebullConnection connection={webullProductionConnection} refreshing={loading}/>}

      {!loading && !visiblePositions.length && !activeTradingVenues.length && !editorOpen && <div className="capital-empty-message"><h2>{environment === "capital" ? "Connect an account to see your capital." : "Start your paper portfolio."}</h2><p className="lede">{environment === "capital" ? "Balances and positions appear after a connection is configured." : "Add a position to explore exposure without real capital."}</p></div>}
      <div className="portfolio-trading-connections"><KalshiTradeTicket
        key={`kalshi-${environment}`}
        environment={environment === "paper" ? "demo" : "production"}
        connection={kalshiConnection}
        onCompleted={onReload}
      />

      <CoinbaseTradeTicket
        key={`coinbase-${environment}`}
        environment={environment === "paper" ? "paper" : "production"}
        connection={coinbaseConnection}
        onCompleted={onReload}
      /></div>

      {editorOpen && <form className="portfolio-editor" id="portfolio-editor" inert={saving} aria-busy={saving} onSubmit={event => void savePosition(event)}>
        <p className="muted">{environmentLabel} · {environment === "paper" ? "Simulated position" : "Manual record"}</p>
        <div className="portfolio-form-grid primary-position-fields">
          <label><span>Symbol</span><input required maxLength={32} value={draft.symbol} onChange={event => setField("symbol", event.target.value)} placeholder="VTI"/></label>
          <label><span>Quantity</span><input required min="0" step="any" type="number" value={draft.quantity} onChange={event => setField("quantity", event.target.value)} placeholder="0"/></label>
          <label><span>Unit cost · USD</span><input min="0" step="any" type="number" value={draft.unitCost} onChange={event => setField("unitCost", event.target.value)} placeholder="Optional"/></label>
          <label><span>Current mark · USD</span><input min="0" step="any" type="number" value={draft.markPrice} onChange={event => setField("markPrice", event.target.value)} placeholder="Optional"/></label>
        </div>
        <div className="position-calculated"><span>Market value</span><strong>{draft.markPrice && draft.quantity ? money(Number(draft.markPrice) * Number(draft.quantity) * (draft.side === "short" ? -1 : 1)) : "—"}</strong></div>
        <details className="position-more" open><summary>More details</summary><div className="portfolio-form-grid">
          <label><span>Asset type</span><select value={draft.assetType} onChange={event => { const type = event.target.value as PortfolioAssetType; setDraft(current => ({ ...current, assetType: type, side: type === "cash" ? "cash" : current.side === "cash" ? "long" : current.side, markPrice: type === "cash" && !current.markPrice ? "1" : current.markPrice })); }}>{portfolioAssetTypes.map(type => <option key={type} value={type}>{type === "fund" ? "ETF / Fund" : type[0].toUpperCase() + type.slice(1)}</option>)}</select></label>
          <label><span>Side</span><select value={draft.side} onChange={event => setField("side", event.target.value as PortfolioSide)}>{portfolioSides.map(side => <option key={side} value={side}>{side[0].toUpperCase() + side.slice(1)}</option>)}</select></label>
          <label><span>Venue</span><input readOnly={environment === "paper"} maxLength={80} value={draft.venue} onChange={event => setField("venue", event.target.value)} placeholder="Manual"/></label>
        </div><label className="portfolio-wide-field"><span>Thesis</span><textarea rows={2} maxLength={4000} value={draft.thesis} onChange={event => setField("thesis", event.target.value)} placeholder="Why this position?"/></label></details>
        <details><summary>Additional notes</summary><div className="portfolio-form-grid"><label><span>Name</span><input maxLength={120} value={draft.name} onChange={event => setField("name", event.target.value)} placeholder="Defaults to symbol"/></label><label><span>State</span><select value={draft.status} onChange={event => setField("status", event.target.value as PortfolioStatus)}>{portfolioStatuses.map(status => <option key={status} value={status}>{status}</option>)}</select></label><label><span>Opened</span><input type="date" value={draft.openedAt} onChange={event => setField("openedAt", event.target.value)}/></label></div><label className="portfolio-wide-field"><span>Notes</span><textarea rows={2} maxLength={4000} value={draft.notes} onChange={event => setField("notes", event.target.value)}/></label></details>
        <details className="portfolio-links"><summary>Risk and research links</summary><div className="portfolio-form-grid"><label><span>Max gross weight · %</span><input min="0" max="100" step="any" type="number" value={draft.riskLimitPct} onChange={event => setField("riskLimitPct", event.target.value)}/></label>{(["sourceIds", "systemIds", "simulationIds", "projectionIds"] as const).map(key => <label key={key}><span>{key.replace("Ids", " links")}</span><input value={draft[key]} onChange={event => setField(key, event.target.value)} placeholder="Comma-separated IDs"/></label>)}</div></details>
        <footer><button type="submit" disabled={saving}>{saving ? "Saving…" : editingId ? "Save changes" : environment === "paper" ? "Save paper position" : "Save manual record"}</button><button type="button" disabled={saving} onClick={closeEditor}>Cancel</button></footer>
      </form>}

      <div className="portfolio-metrics" aria-label="Portfolio summary">
        <div><span>Net marked value</span><strong>{money(summary.netMarkedValue)}</strong></div>
        <div><span>Gross exposure</span><strong>{money(summary.grossExposure)}</strong></div>
        <div><span>Unrealized P/L</span><strong>{money(summary.unrealizedPnl)}</strong></div>
        <div><span>Cash</span><strong>{money(summary.cash)}</strong></div>
      </div>

      <div className="portfolio-status-line">
        <span>Long {money(summary.longExposure)}</span>
        <span>Short {money(summary.shortExposure)}</span>
        <span>{summary.missingMarkCount} missing marks</span>
        <span>{summary.riskBreachCount} risk breaches</span>
      </div>

      <div className="portfolio-register" aria-busy={loading}>
        {loading && <p className="portfolio-empty">Loading {environmentLabel.toLowerCase()} state…</p>}
        {!loading && !visiblePositions.length && !loadError && (
          <div className="portfolio-empty">
            <strong>{environment === "paper" ? "No paper positions returned." : "No capital records yet."}</strong>
            <span>{environment === "paper"
              ? "Add a manual position or use a connected paper venue."
              : "Add a position, cash balance, or watch item. Nothing is prefilled or inferred."}</span>
            <button type="button" onClick={startNew}>{environment === "paper" ? "Add paper position" : "Add first position"}</button>
          </div>
        )}
        {!loading && visiblePositions.filter(position => !selectedPositionId || selectedPositionId === position.id).map((position) => {
          const positionMetrics = metrics.get(position.id);
          const intelligenceCount = position.sourceIds.length + position.systemIds.length
            + position.simulationIds.length + position.projectionIds.length;
          return (
            <details className="portfolio-position" id={`portfolio-${position.id}`} key={position.id} open={selectedPositionId === position.id}>
              <summary onClick={event => { event.preventDefault(); setSelectedPositionId(position.id); }}>
                <span className="portfolio-index" aria-hidden="true" />
                <div className="portfolio-identity">
                  <strong>{position.symbol}</strong>
                  <span>{position.name}{position.origin === "webull-paper"
                    ? " · sandbox"
                    : position.origin === "polymarket"
                      ? " · public"
                      : position.origin === "polymarket-paper"
                        ? " · simulated"
                        : position.origin === "kalshi-demo" || position.origin === "kalshi-paper"
                          ? " · paper"
                          : position.origin === "kalshi-production"
                            ? " · real"
                            : position.origin === "coinbase-paper"
                              ? " · paper"
                              : position.origin === "coinbase-production"
                                ? " · real"
                            : ""}</span>
                </div>
                <span className="portfolio-type">{position.side} · {position.assetType}</span>
                <span className="portfolio-quantity">{number(position.quantity)}</span>
                <span className="portfolio-mark">{money(positionMetrics?.marketValue ?? null)}</span>
                <span className={positionMetrics?.overRiskLimit ? "portfolio-weight breach" : "portfolio-weight"}>
                  {percent(positionMetrics?.grossWeightPct ?? null)}
                </span>
                <span className="portfolio-record-state">{position.status}</span>
              </summary>
              <div className="portfolio-position-detail">
                <div className="position-value-hero"><strong>{money(positionMetrics?.marketValue ?? null)}</strong><span>{number(position.quantity)} {position.assetType === "fund" || position.assetType === "equity" ? "shares" : "units"} · {environmentLabel} · {position.name}</span></div>
                <dl className="portfolio-position-metrics">
                  <div><dt>Unit cost</dt><dd>{money(position.unitCost)}</dd></div>
                  <div><dt>Current mark</dt><dd>{money(position.markPrice)}</dd></div>
                  <div><dt>Unrealized P/L</dt><dd>{money(positionMetrics?.unrealizedPnl ?? null)}</dd></div>
                  <div><dt>Risk cap</dt><dd>{percent(position.riskLimitPct)}</dd></div>
                  <div><dt>Venue</dt><dd>{position.venue || "—"}</dd></div>
                  <div><dt>{position.externalAccountLabel ? "Account" : "Opened"}</dt><dd>{position.externalAccountLabel || position.openedAt || "—"}</dd></div>
                </dl>
                <div className="portfolio-thesis">
                  <section>
                    <span>Thesis</span>
                    <p>{position.thesis || "No thesis recorded."}</p>
                  </section>
                  <section>
                    <span>Intelligence links</span>
                    <p>{intelligenceCount ? [
                      ...position.sourceIds.map((id) => `source:${id}`),
                      ...position.systemIds.map((id) => `system:${id}`),
                      ...position.simulationIds.map((id) => `simulation:${id}`),
                      ...position.projectionIds.map((id) => `projection:${id}`),
                    ].join(" · ") : "No links recorded."}</p>
                  </section>
                </div>
                {position.notes && <p className="portfolio-notes">{position.notes}</p>}
                <footer>
                  <span>{position.syncedAt ? "Synced" : "Updated"} {updatedLabel(position.syncedAt ?? position.updatedAt)}</span>
                  {isEditablePosition(position) ? (
                    <div>
                      <button type="button" onClick={() => startEdit(position)}>Edit</button>
                      <button type="button" onClick={() => void removePosition(position)}>Delete</button>
                    </div>
                  ) : <strong className="portfolio-read-only">Synchronized · {
                    position.origin === "kalshi-production"
                      ? "real"
                      : position.origin === "kalshi-demo" || position.origin === "kalshi-paper"
                        ? "paper"
                        : position.origin === "coinbase-production"
                          ? "real"
                          : position.origin === "coinbase-paper"
                            ? "paper"
                        : position.origin === "polymarket"
                          ? "public"
                          : "sandbox"
                  }</strong>}
                </footer>
              </div>
            </details>
          );
        })}
      </div>

      <p className="boundary">
        {environment === "paper"
          ? "Paper is isolated from real capital: Coinbase and Kalshi use live market data with private simulated ledgers and manual two-step confirmation; Webull sandbox is read-only; Polymarket positions remain local simulations."
          : "Real capital ledger. Manual Coinbase and Kalshi orders require an exact five-minute preview and owner confirmation. The Kalshi pilot has separate limits and activation in Autonomy. Core chat cannot submit orders. Global Polymarket data remains read-only."}
      </p>
      </>}
    </section>
  );
}
